import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
  Typography,
  Alert,
  Box,
} from '@mui/material'
import { useState } from 'react'
import { StructuredQuestion } from '../interfaces/questions'
import { computeSubNumber } from '../utils/questionUtils'

interface InsertQuestionDialogProps {
  open: boolean
  atIndex: number
  position: 'above' | 'below'
  allQuestions: StructuredQuestion[]
  onConfirm: (choice: 'sub' | 'renumber') => void
  onClose: () => void
}

export default function InsertQuestionDialog({
  open,
  atIndex,
  position,
  allQuestions,
  onConfirm,
  onClose,
}: InsertQuestionDialogProps) {
  const [choice, setChoice] = useState<'sub' | 'renumber'>('renumber')

  // The question the admin clicked on (for the dialog wording)
  const clickedQuestion = allQuestions[atIndex]

  // The question that will sit directly above the inserted question (for sub-number computation)
  const referenceIndex = position === 'above' ? atIndex - 1 : atIndex
  const referenceQuestion =
    referenceIndex >= 0 ? allQuestions[referenceIndex] : undefined

  // Detect insert-above-sub: pushing existing subs down
  const clickedSuffix =
    clickedQuestion?.question_number.match(/[a-z]$/i)?.[0] ?? ''
  const isInsertAboveSub = position === 'above' && !!clickedSuffix

  // Check if shifting subs would overflow past 'z'
  const wouldOverflow = isInsertAboveSub && (() => {
    const base = clickedQuestion!.question_number.replace(/[a-z]$/i, '')
    return allQuestions.some((q) => q.question_number === `${base}z`)
  })()

  const proposedSubNumber = isInsertAboveSub
    ? wouldOverflow
      ? null // can't shift — 'z' already exists
      : clickedQuestion!.question_number
    : referenceQuestion
      ? computeSubNumber(referenceQuestion.question_number, allQuestions)
      : null
  const canSub = proposedSubNumber !== null

  const insertIndex = position === 'above' ? atIndex : atIndex + 1
  const integersBefore = allQuestions
    .slice(0, insertIndex)
    .filter((q) => !q.question_number.match(/[a-z]$/i)).length
  const proposedRenumber = String(integersBefore + 1)

  const handleConfirm = () => {
    onConfirm(choice)
    setChoice('renumber')
  }

  const handleClose = () => {
    setChoice('renumber')
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>How should the new question be numbered?</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Inserting {position} question {clickedQuestion?.question_number ?? '1'}
        </Alert>
        <FormControl>
          <RadioGroup
            value={choice}
            onChange={(e) => setChoice(e.target.value as 'sub' | 'renumber')}
          >
            <FormControlLabel
              value="renumber"
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body2">
                    Renumber all — number new question as{' '}
                    <strong>{proposedRenumber}</strong> and update following
                    questions sequentially
                  </Typography>
                </Box>
              }
            />
            <FormControlLabel
              value="sub"
              disabled={!canSub}
              control={<Radio />}
              label={
                <Box>
                  <Typography variant="body2">
                    Sub-question — number new question as{' '}
                    <strong>{proposedSubNumber ?? '—'}</strong>
                    {isInsertAboveSub
                      ? ', shift existing subs down'
                      : ', leave other question numbers unchanged'}
                  </Typography>
                  {!canSub && (
                    <Typography variant="caption" color="text.secondary">
                      No reference question available
                    </Typography>
                  )}
                </Box>
              }
            />
          </RadioGroup>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm}>
          Insert Question
        </Button>
      </DialogActions>
    </Dialog>
  )
}
